import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import type {
  StepParameterResponse,
  StepParameterAttributes,
} from 'types/StepParameter';
import { ParameterWithStepParameterID } from 'types/Parameters';

const getStepParametersByStepID = async (stepID: string | null | undefined) => {
  return fetchWrapper<StepParameterResponse>({
    url: `SparkStepParameters?filter[sparkStepID]=${stepID}&include=parameter`,
    method: 'GET',
  });
};

export function useStepParametersByStepID(stepID: string | null | undefined) {
  const queryResult = useQuery(
    ['SparkStepParameters', stepID],
    () => getStepParametersByStepID(stepID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Step Parameters: ${error.message}`);
        }
      },
      ...CACHE_AND_STALE_TIME,
    }
  );
  const { data } = queryResult;
  const { included: includedParameters, data: stepParametersResponse } =
    data || {};

  const parameters: ParameterWithStepParameterID[] =
    includedParameters?.map((parameter) => {
      const stepParameterID = stepParametersResponse?.find(
        (stepParameter) => stepParameter.attributes.parameterID === parameter.id
      )?.id;
      return {
        ...parameter,
        attributes: {
          ...parameter.attributes,
          stepParameterID: Number(stepParameterID),
        },
      };
    }) || [];

  const parameterIdByNameMap = parameters?.reduce<
    Record<string, { stepParameterID: number; parameterID: string }>
  >((acc, curr) => {
    return {
      ...acc,
      [curr.attributes.name]: {
        parameterID: curr.id,
        stepParameterID: curr.attributes.stepParameterID,
      },
    };
  }, {});

  const stepParameterByIdMap = stepParametersResponse?.reduce<
    Record<string, StepParameterAttributes>
  >((acc, curr) => {
    return {
      ...acc,
      [curr.id]: curr.attributes,
    };
  }, {});

  return {
    parameters,
    parameterIdByNameMap,
    stepParameterByIdMap,
    stepParameters: stepParametersResponse,
    ...queryResult,
  };
}
